import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import PublicFooter from '@/components/PublicFooter';

const CATEGORIES = ['Barber', 'Nails', 'Beauty', 'Makeup', 'Massage'];

export default function PartnerSignup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    business_name: '',
    category: 'Beauty',
    base_postcode: 'HA1',
    accepted_independent_contractor: false,
    accepted_partner_terms: false,
    accepted_self_responsibility: false,
    accepted_no_off_platform: false,
    accepted_booking_hours: false,
  });

  const allTermsAccepted = useMemo(
    () =>
      form.accepted_independent_contractor &&
      form.accepted_partner_terms &&
      form.accepted_self_responsibility &&
      form.accepted_no_off_platform &&
      form.accepted_booking_hours,
    [form.accepted_independent_contractor, form.accepted_partner_terms, form.accepted_self_responsibility, form.accepted_no_off_platform, form.accepted_booking_hours],
  );

  // All hooks must be declared BEFORE any conditional early return.
  // Previously `useCallback` lived after `if (user) return <Navigate>`,
  // which violated the Rules of Hooks and could trigger React
  // concurrent-rendering crashes after signup.
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await api.post('/auth/partner/signup', {
          name: form.name,
          email: form.email.trim().toLowerCase(),
          phone: form.phone,
          password: form.password,
          business_name: form.business_name,
          category: form.category,
          base_postcode: form.base_postcode.toUpperCase(),
          accepted_independent_contractor: form.accepted_independent_contractor,
          accepted_partner_terms: form.accepted_partner_terms,
          accepted_self_responsibility: form.accepted_self_responsibility,
          accepted_no_off_platform: form.accepted_no_off_platform,
          accepted_booking_hours: form.accepted_booking_hours,
          accepted_terms_version: 'v1.0',
        });
        // Auto-login: signup endpoint sets the httpOnly cookie. Re-validate session
        // via window reload so AuthProvider picks up the new user role.
        toast.success('Application submitted! Pending admin approval.');
        window.location.href = '/partner/dashboard';
      } catch (err) {
        setError(err?.response?.data?.detail || 'Signup failed');
      } finally {
        setSubmitting(false);
      }
    },
    [form],
  );

  if (user && user.role === 'partner') {
    return <Navigate to="/partner/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate('/partner/login')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-4"
          data-testid="partner-signup-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to login
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Become a Partner</h1>
            <p className="text-sm text-slate-500">Join the BeautyMateUK network</p>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Your details</CardTitle>
            <CardDescription>You'll be reviewed by our team before going live.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="signup-name" value={form.name} onChange={update('name')} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="signup-email" value={form.email} onChange={update('email')} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" data-testid="signup-phone" value={form.phone} onChange={update('phone')} required className="h-11" placeholder="+44 7700 900000" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="signup-password" value={form.password} onChange={update('password')} required minLength={6} className="h-11" />
              </div>

              <div className="h-px bg-slate-100 my-3" />

              <div className="space-y-2">
                <Label htmlFor="business">Business name</Label>
                <Input id="business" data-testid="signup-business" value={form.business_name} onChange={update('business_name')} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={update('category')}>
                  <SelectTrigger className="h-11" data-testid="signup-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Base postcode</Label>
                <Input id="postcode" data-testid="signup-postcode" value={form.base_postcode} onChange={update('base_postcode')} required maxLength={8} className="h-11 font-mono" />
              </div>

              <div className="h-px bg-slate-100 my-3" />
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Partner Terms</p>
              <div className="space-y-3">
                {[
                  { key: 'accepted_independent_contractor', label: 'I confirm I am an independent self-employed service provider and not an employee of BeautyMateUK.' },
                  { key: 'accepted_partner_terms', label: (<>I agree to the BeautyMateUK <Link to="/partner-terms" target="_blank" className="text-rose-700 hover:underline">Partner Terms</Link>, <Link to="/terms" target="_blank" className="text-rose-700 hover:underline">Terms & Conditions</Link>, <Link to="/privacy" target="_blank" className="text-rose-700 hover:underline">Privacy Policy</Link>, and <Link to="/safety-policy" target="_blank" className="text-rose-700 hover:underline">Safety Policy</Link>.</>) },
                  { key: 'accepted_self_responsibility', label: 'I confirm I am responsible for my own tax, National Insurance, licences, insurance, qualifications, hygiene, tools, equipment, and legal compliance.' },
                  { key: 'accepted_no_off_platform', label: 'I agree not to arrange off-platform bookings with BeautyMateUK customers.' },
                  { key: 'accepted_booking_hours', label: 'I agree not to perform BeautyMateUK bookings outside 06:00 to 23:00.' },
                ].map((c) => (
                  <div key={c.key} className="flex items-start gap-2">
                    <Checkbox
                      id={`partner-${c.key}`}
                      data-testid={`partner-signup-${c.key.replace(/_/g, '-')}`}
                      checked={form[c.key]}
                      onCheckedChange={(checked) => setForm((f) => ({ ...f, [c.key]: !!checked }))}
                      className="mt-0.5"
                    />
                    <Label htmlFor={`partner-${c.key}`} className="text-xs text-slate-600 leading-relaxed cursor-pointer">{c.label}</Label>
                  </div>
                ))}
              </div>

              {error && (
                <div data-testid="signup-error" className="text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !allTermsAccepted}
                data-testid="signup-submit"
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base disabled:opacity-50"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Submit application'}
              </Button>
              <p className="text-[11px] text-center text-slate-400">
                Your application is reviewed by an admin. You can sign in and start setting up your profile right away.
              </p>
            </form>
          </CardContent>
        </Card>
        <PublicFooter variant="minimal" />
      </div>
    </div>
  );
}
